"use client";

/**
 * <AppSidebar> — ChatGPT/Claude 스타일 영구 사이드바
 *
 * 락인 핵심: 내가 분석한 모든 기업이 왼쪽에 상주 → 50개 쌓이면 이사 불가.
 *
 * 구성:
 *   - [+ 새 분석] 버튼
 *   - 검색창 (⌘K)
 *   - 즐겨찾기 섹션 (localStorage 기반)
 *   - 최근 섹션 (saved 리포트 · 최근순)
 *   - Row: 티커 · 기업명 · 등락률(오늘) · 상대시간
 *   - 하단: 프로필 · tier 배지 · 설정
 *
 * 키보드: ⌘K(검색) · ⌘N(새 분석) · ⌘B(토글) · j/k(선택)
 */

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import {
  Plus, Search, Star, Star as StarFilled, X, PanelLeft,
  Settings, LogIn, Sparkles, Menu, MoreHorizontal, Trash2,
  ChevronDown, ChevronRight,
  Home, Globe, Building2, LineChart, Newspaper,
  Briefcase, GraduationCap, Check, LayoutDashboard,
  BarChart3,
  Sun, Moon, Users,
} from "lucide-react";
import { BiteIcon, EntryIcon } from "@/components/icons/brand-icons";

/** 사이드바 전용 ENTRY 아이콘 — ENTRY path 가 viewBox 거의 가득 채워서 시각 무게가 무거움.
 *  내부 아이콘 13px, 외부 박스는 16x16 (다른 lucide w-4 h-4 와 동일) — 라벨 들여쓰기 정렬 보존. */
function SidebarEntryIcon({ className }: { className?: string }) {
  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <EntryIcon size={13} />
    </span>
  );
}
import { isCommunityEnabled } from "@/lib/feature-flags";
import { useTheme } from "next-themes";
import type { ComponentType } from "react";
import { toast } from "sonner";
import { useDashboardUI } from "@/contexts/dashboard-ui-context";
import { WidgetPalette } from "@/components/dashboard/widget-palette";
import type { WidgetType } from "@/components/dashboard/types";

interface SavedItem {
  jobId: string;
  ticker: string;
  companyName?: string;
  market?: "kr" | "us";
  score?: number | null;
  createdAt?: string | null;
  savedAt?: string | null;
}

interface PriceInfo {
  price: number | null;
  changePercent: number | null;
}

interface TickerGroup {
  ticker: string;
  companyName?: string;
  market?: "kr" | "us";
  versions: SavedItem[]; // 최근순 정렬 (versions[0]이 최신)
  latest: SavedItem;
}

const FAV_STORAGE_KEY = "bullsresearch:favorites";
const COLLAPSE_STORAGE_KEY = "bullsresearch:sidebar-collapsed";
const EXPANDED_STORAGE_KEY = "bullsresearch:sidebar-expanded";

/** 사이드바 고정 메뉴 — 라우트 있는 건 enabled · 없는 건 toast 안내
 *  scrollAnchor: 같은 페이지(/) 안의 anchor id — 클릭 시 smooth scroll
 *  (홈/대시보드 통합 — 사용자 결정: 둘이 같은 1-page 페이지의 다른 섹션) */
interface NavItem {
  label: string;
  icon: ComponentType<{ className?: string }>;
  href: string;
  enabled: boolean;
  scrollAnchor?: string;   // / 페이지 안 element id
  premiumTone?: "red" | "blue"; // 유료 강조 — BITE: red, ENTRY: blue (좌측 thin bar + label color)
}
// 사용자 결정 2026-05-09 rebuild: BITE / ENTRY 별도 서비스 개념 폐기.
// "기업 분석" 단일 진입에서 분기. 사이드바도 3개 항목으로 단순화.
// 사용자 결정 2026-05-21: 모바일 footer nav (BottomNav) 에서도 재사용 위해 export
export const NAV_ITEMS: NavItem[] = [
  { label: "홈",                icon: Home,           href: "/",           enabled: true, scrollAnchor: "home-hero" },
  { label: "대시보드",          icon: LayoutDashboard,href: "/#dashboard", enabled: true, scrollAnchor: "home-dash" },
  { label: "기업 분석",         icon: BarChart3,      href: "/analyze",    enabled: true },
  // 사용자 결정 2026-05-17: 기업 분석 하위 메뉴 2개 (Coming Soon)
  { label: "물린 종목 분석",    icon: BiteIcon,       href: "/bite",       enabled: false },
  { label: "진입 시점 분석",    icon: EntryIcon,      href: "/entry",      enabled: false },
  // 사용자 결정 2026-05-17: "포트폴리오" 제거
];
// 사용자 결정 2026-05-17: 커뮤니티 활성화 (이전 enabled:false 폐기)
export const COMMUNITY_NAV: NavItem = {
  label: "커뮤니티", icon: Users, href: "/community", enabled: true,
};

function timeAgo(iso?: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "방금";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}분`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}일`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}주`;
  return new Date(iso).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" });
}

// SSR safe — first render 는 빈 string (서버/client 일치), client mount 후 진짜 값.
// 근본 원인: timeAgo 가 Date.now() 사용 → SSR 시점 vs client hydration 시점 차이로
// "5분" vs "3시간" 같은 mismatch → React #418 (사용자 보고 2026-05-13).
function useTimeAgo(iso?: string | null): string {
  const [text, setText] = useState("");
  useEffect(() => {
    setText(timeAgo(iso));
  }, [iso]);
  return text;
}

function TimeAgoSpan({ iso, className }: { iso?: string | null; className?: string }) {
  const text = useTimeAgo(iso);
  return <span className={className} suppressHydrationWarning>{text}</span>;
}

export function AppSidebar() {
  const { auth, loading: authLoading } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  // 다크 시 logo-dark.png (흰 황소), 라이트 시 logo-bull.png (검정 outline 황소)
  // — PR #247 에서 빨간 원 SVG 로 통일했었으나 사용자 요청으로 원래 logo-bull.png 복원
  const { resolvedTheme } = useTheme();
  // 통합 mounted gate — SSR/client 다른 모든 값을 mount 후만 사용 (#418 fix).
  // 사용자 보고 2026-05-13/14. 근본 원인 누적:
  //   1. <img src={isDark?...}/> = useTheme().resolvedTheme SSR=undef → client=value
  //   2. searchParams.get('id') = SSR=null → client=value (Next.js 15 prerender)
  //   둘 다 attribute/text 분기로 mismatch → hydrated 전엔 default 강제.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => { setHydrated(true); }, []);
  const isDark = hydrated && resolvedTheme === "dark";

  const isLoggedIn = auth && "loggedIn" in auth && auth.loggedIn;
  const currentJobId = hydrated ? (searchParams.get("id") || "") : "";

  // 대시보드 편집 모드 감지 — /dashboard 에서만 유효
  const dashUI = useDashboardUI();
  const isDashboard = pathname === "/dashboard" || pathname?.startsWith("/dashboard/");
  const paletteMode = Boolean(isDashboard && dashUI.editMode);

  const [items, setItems] = useState<SavedItem[]>([]);
  const [prices, setPrices] = useState<Record<string, PriceInfo>>({});
  const [pricesLoaded, setPricesLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  // 사용자 결정 2026-05-09 — 디폴트 collapsed (localStorage 값 없을 때).
  // 펼치고 싶으면 사용자가 토글, 그 선택은 영구.
  const [collapsed, setCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [expandedTickers, setExpandedTickers] = useState<Set<string>>(new Set());
  const [activeAnchor, setActiveAnchor] = useState<string>("home-hero");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 홈 페이지(/) 의 hero / dashboard 영역을 IntersectionObserver 로 추적해서
  // 사이드바 active state 자동 갱신 (홈 ↔ 대시보드 통합)
  useEffect(() => {
    if (pathname !== "/" && pathname !== "") return;
    // 초기 hash 반영
    if (typeof window !== "undefined" && window.location.hash === "#dashboard") {
      setActiveAnchor("home-dash");
    } else {
      setActiveAnchor("home-hero");
    }
    const targets = ["home-hero", "home-dash"]
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];
    if (targets.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        // viewport 의 ~중간을 가장 많이 차지하는 섹션을 active 로
        let best: { id: string; ratio: number } | null = null;
        for (const e of entries) {
          if (e.isIntersecting && (!best || e.intersectionRatio > best.ratio)) {
            best = { id: e.target.id, ratio: e.intersectionRatio };
          }
        }
        if (best) {
          setActiveAnchor(best.id);
          // URL hash 동기화 (silent)
          const hash = best.id === "home-dash" ? "#dashboard" : "";
          if (window.location.hash !== hash) {
            history.replaceState(null, "", `/${hash}`);
          }
        }
      },
      { rootMargin: "-30% 0px -30% 0px", threshold: [0.1, 0.5, 0.9] }
    );
    targets.forEach((t) => obs.observe(t));
    return () => obs.disconnect();
  }, [pathname]);

  // 초기 로드 — localStorage
  useEffect(() => {
    try {
      const fav = localStorage.getItem(FAV_STORAGE_KEY);
      if (fav) setFavorites(new Set(JSON.parse(fav)));
      const col = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      // 명시적으로 '0' 면 펼침. 그 외 (값 없음 또는 '1') 는 디폴트 collapsed 유지.
      if (col === "0") setCollapsed(false);
      const exp = localStorage.getItem(EXPANDED_STORAGE_KEY);
      if (exp) setExpandedTickers(new Set(JSON.parse(exp)));
    } catch { /* ignore */ }
  }, []);

  // saved 리포트 로드
  const refresh = useCallback(async () => {
    if (!isLoggedIn) { setItems([]); return; }
    setLoading(true);
    try {
      const res = await api<{ items: SavedItem[] }>("/api/ticker/saved");
      const list = (res.items || []).filter((x) => x && x.jobId && x.ticker);
      // 최근순 정렬
      list.sort((a, b) => {
        const ta = new Date(a.savedAt || a.createdAt || 0).getTime();
        const tb = new Date(b.savedAt || b.createdAt || 0).getTime();
        return tb - ta;
      });
      setItems(list);
    } catch { /* 무시 — 빈 상태로 fallback */ } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => { refresh(); }, [refresh]);

  // 등락률 배치 로드 (items 변경 시)
  // api() wrapper 사용 — CSRF 토큰 자동 주입 · 403 시 재시도까지 내장
  // (raw fetch 로 CSRF 헤더 없이 POST 하면 csrfTokenProtection 미들웨어가 403 차단)
  useEffect(() => {
    if (items.length === 0) { setPricesLoaded(true); return; }
    const tickers = [...new Set(items.map((i) => i.ticker))];
    let cancelled = false;
    setPricesLoaded(false);
    (async () => {
      try {
        const data = await api<{ results?: Record<string, { price: number | null; changePercent: number | null }> }>(
          "/api/ticker/prices",
          { method: "POST", body: { tickers } }
        );
        if (cancelled) return;
        const map: Record<string, PriceInfo> = {};
        Object.entries(data.results || {}).forEach(([k, v]) => {
          map[k] = { price: v.price, changePercent: v.changePercent };
        });
        if (!cancelled) setPrices(map);
      } catch { /* 무시 — 가격 표시만 생략 */ } finally {
        if (!cancelled) setPricesLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [items]);

  // 즐겨찾기 토글
  const toggleFav = useCallback((jobId: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      try { localStorage.setItem(FAV_STORAGE_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // 사이드바 접기 토글
  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // 버전 그룹 펼침 토글
  const toggleExpand = useCallback((ticker: string) => {
    setExpandedTickers((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      try { localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // 키보드 shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === "k" || e.key === "K") {
        e.preventDefault();
        setMobileOpen(true);
        if (collapsed) toggleCollapse();
        setTimeout(() => searchInputRef.current?.focus(), 50);
      } else if (e.key === "n" || e.key === "N") {
        // ⌘N 은 브라우저가 가로챌 수 있음 (새 창) — 가볍게 처리
        if (e.shiftKey) return;
        e.preventDefault();
        router.push("/analyze");
        setMobileOpen(false);
      } else if (e.key === "b" || e.key === "B") {
        e.preventDefault();
        toggleCollapse();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [collapsed, toggleCollapse, router]);

  // 라우트 변경 시 모바일 drawer 닫기
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  // 검색 필터링
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter((i) =>
      i.ticker.toLowerCase().includes(q) ||
      (i.companyName || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  // ticker 단위 그룹핑 — 같은 기업의 여러 분석을 버전으로 묶음
  const groups = useMemo<TickerGroup[]>(() => {
    const map = new Map<string, SavedItem[]>();
    filtered.forEach((i) => {
      const list = map.get(i.ticker) || [];
      list.push(i);
      map.set(i.ticker, list);
    });
    const result: TickerGroup[] = [];
    map.forEach((versions, ticker) => {
      const latest = versions[0]; // items는 최근순 이미 정렬됨
      result.push({
        ticker,
        companyName: latest.companyName,
        market: latest.market,
        versions,
        latest,
      });
    });
    result.sort((a, b) => {
      const ta = new Date(a.latest.savedAt || a.latest.createdAt || 0).getTime();
      const tb = new Date(b.latest.savedAt || b.latest.createdAt || 0).getTime();
      return tb - ta;
    });
    return result;
  }, [filtered]);

  // 즐겨찾기: 그룹의 latest(최신) 버전 jobId 기준
  const favoriteGroups = groups.filter((g) => favorites.has(g.latest.jobId));
  const recentGroups = groups.filter((g) => !favorites.has(g.latest.jobId));

  async function handleDelete(jobId: string) {
    if (!confirm("이 리포트를 사이드바에서 제거할까요? (리포트 자체는 유지)")) return;
    try {
      await api<{ ok: boolean }>(`/api/ticker/saved/${encodeURIComponent(jobId)}`, { method: "DELETE" });
      setItems((prev) => prev.filter((x) => x.jobId !== jobId));
      toggleFav(jobId);  // 즐겨찾기도 정리
      toast.success("제거됨");
    } catch { toast.error("제거 실패"); }
    setMenuOpenId(null);
  }

  /** 사이드바에서 위젯 추가 트리거 — 대시보드 페이지가 window event 로 받음 */
  const handleAddWidget = useCallback((type: WidgetType, options?: Record<string, unknown>) => {
    window.dispatchEvent(new CustomEvent("bs:dashboard:add-widget", {
      detail: { type, options },
    }));
  }, []);

  // === 렌더 ===
  // palette 모드면 강제 펼침 · "palette" 클래스로 전용 스타일 트리거
  // 기존 사이드바 구조 (top · nav · bottom) 는 그대로 유지 · 리스트 영역만 위젯 갤러리로 변형
  const effectiveCollapsed = paletteMode ? false : collapsed;
  const sidebarClass = `app-sb ${effectiveCollapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""} ${paletteMode ? "palette" : ""}`;

  return (
    <>
      {/* 모바일 hamburger */}
      <button
        type="button"
        className={`app-sb-mobile-toggle ${mobileOpen ? "is-hidden" : ""}`}
        onClick={() => setMobileOpen((v) => !v)}
        aria-label="Toggle sidebar"
      >
        <Menu className="w-4 h-4" />
      </button>

      {/* backdrop (모바일) */}
      {mobileOpen && <div className="app-sb-backdrop" onClick={() => setMobileOpen(false)} />}

      <aside className={sidebarClass}>
        {/*
          Top: 로고 · 접기 토글
          UX 규칙:
            - 펼쳐진 상태(default): 로고만 보이다가 sb-top hover 시 접기 버튼으로 교체
            - 접힌 상태: 토글 버튼(네모)만 · 가운데 정렬 · 클릭하면 펼쳐짐
        */}
        <div className="sb-top">
          <Link href="/" className="sb-logo">
            {/* Bite 리브랜딩 (사용자 결정 2026-05-17). 다크 → bite-logo-white, 라이트 → bite-logo */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={isDark ? "/bite-logo-white.png" : "/bite-logo.png"}
              alt="Bite"
              className="sb-logo-mark"
            />
            {!collapsed && <span className="sb-logo-text">Bite</span>}
          </Link>
          <button
            className="sb-collapse"
            onClick={toggleCollapse}
            aria-label={collapsed ? "사이드바 펼치기" : "사이드바 접기"}
          >
            <PanelLeft className="w-4 h-4" />
          </button>
        </div>

        {/* Main navigation — 홈 / 거시경제 / 기업 분석 / 차트 / 포트폴리오 / 뉴스 / 학습 */}
        {/* 편집 모드(palette) 에선 숨김 — 위젯 갤러리에 집중 */}
        {!paletteMode && (
        <nav className="sb-nav" aria-label="메인 메뉴">
          {(() => {
            const items = [...NAV_ITEMS];
            // 사용자 결정 #4: 커뮤니티 — staging build 또는 admin 만 노출
            const showCommunity = isCommunityEnabled(
              isLoggedIn ? auth.email : null,
              isLoggedIn ? auth.isAdmin : null,
            );
            if (showCommunity) items.push(COMMUNITY_NAV);
            return items;
          })().map((item) => {
            const Icon = item.icon;
            // active 판정 — / 페이지에선 scrollAnchor 별 active 나누기 (홈/대시보드)
            const onHome = pathname === "/" || pathname === "";
            let active: boolean;
            if (item.scrollAnchor && onHome) {
              active = activeAnchor === item.scrollAnchor;
            } else {
              active = pathname === item.href || pathname.startsWith(item.href + "/");
            }
            // 유료 강조 클래스 (BITE: red · ENTRY: blue) — premiumTone 별 시각 구분
            const premiumClass = item.premiumTone === "red"
              ? "sb-nav-item-premium sb-nav-item-premium-red"
              : item.premiumTone === "blue"
              ? "sb-nav-item-premium sb-nav-item-premium-blue"
              : "";
            if (!item.enabled) {
              return (
                <button
                  key={item.href}
                  type="button"
                  className={`sb-nav-item sb-nav-item-coming ${premiumClass}`}
                  title={collapsed ? `${item.label} · 곧 출시` : undefined}
                  onClick={() => toast.info(`${item.label} — 곧 출시됩니다`)}
                >
                  <Icon className="w-4 h-4" />
                  {/* 사용자 결정 2026-05-09 — 라벨 항상 표시. badge 는 expanded 만 */}
                  <span className="sb-nav-label">{item.label}</span>
                  {!collapsed && <span className="sb-nav-badge">soon</span>}
                </button>
              );
            }
            const handleClick = (e: React.MouseEvent) => {
              // 같은 페이지(/) 의 anchor 면 smooth scroll
              if (item.scrollAnchor && onHome) {
                e.preventDefault();
                const el = document.getElementById(item.scrollAnchor);
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  // URL hash 동기화 (페이지 리로드 X)
                  const newHash = item.scrollAnchor === "home-dash" ? "#dashboard" : "";
                  history.replaceState(null, "", `/${newHash}`);
                  setActiveAnchor(item.scrollAnchor);
                }
              }
            };
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={handleClick}
                className={`sb-nav-item ${active ? "is-active" : ""} ${premiumClass}`}
                title={collapsed ? item.label : undefined}
              >
                <Icon className="w-4 h-4" />
                {/* 사용자 결정 2026-05-09 — collapsed 시에도 작은 라벨 (아이콘 아래 stack) */}
                <span className="sb-nav-label">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        )}

        {!collapsed && paletteMode && (
          <>
            {/* 편집 모드 헤더 (검색/새분석 자리 대체) */}
            <div className="sb-palette-head">
              <div className="sb-palette-title">
                <LayoutDashboard className="w-4 h-4" />
                <span>위젯 갤러리</span>
              </div>
              <button
                type="button"
                className="sb-palette-done"
                onClick={() => dashUI.setEditMode(false)}
                title="편집 완료"
              >
                <Check className="w-3.5 h-3.5" />
                <span>완료</span>
              </button>
            </div>
            {/* 기업 목록 자리에 위젯 갤러리 렌더 */}
            <div className="sb-palette-body">
              <WidgetPalette onAdd={handleAddWidget} />
            </div>
          </>
        )}

        {!collapsed && !paletteMode && (
          <>
            {/* Search */}
            <div className="sb-search">
              <Search className="w-3.5 h-3.5" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="내 분석 검색"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") setQuery(""); }}
              />
              {query && (
                <button onClick={() => setQuery("")}><X className="w-3 h-3" /></button>
              )}
              {!query && <kbd className="sb-kbd">⌘K</kbd>}
            </div>

            {/* New analysis — 검색 아래에 배치 · 껍질 없는 가벼운 row */}
            <Link href="/analyze" className="sb-new">
              <Plus className="w-4 h-4" />
              <span>새 분석</span>
              <kbd className="sb-kbd">⌘N</kbd>
            </Link>

            {/* List */}
            <div className="sb-list">
              {!isLoggedIn && !authLoading && (
                <div className="sb-empty">
                  <LogIn className="w-4 h-4 sb-empty-icon" />
                  <p>로그인하면 분석한 기업이<br />여기 쌓여요.</p>
                  <Link href="/auth/google" className="sb-empty-cta">
                    로그인 →
                  </Link>
                </div>
              )}

              {isLoggedIn && !loading && items.length === 0 && (
                <div className="sb-empty">
                  <p>첫 분석을 시작해보세요</p>
                  <div className="sb-sample">
                    {["NVDA", "005930", "TSLA"].map((t) => (
                      <Link key={t} href={`/analyze?ticker=${t}`} className="sb-sample-chip">
                        {t}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {favoriteGroups.length > 0 && (
                <>
                  <div className="sb-section-h">
                    <StarFilled className="w-3 h-3" fill="currentColor" />
                    즐겨찾기
                  </div>
                  {favoriteGroups.map((group) => (
                    <SidebarGroup
                      key={group.ticker}
                      group={group}
                      price={prices[group.ticker]}
                      pricesLoaded={pricesLoaded}
                      currentJobId={currentJobId}
                      favorited
                      expanded={expandedTickers.has(group.ticker)}
                      onToggleExpand={() => toggleExpand(group.ticker)}
                      menuOpenId={menuOpenId}
                      onMenuToggle={setMenuOpenId}
                      onFavToggle={() => toggleFav(group.latest.jobId)}
                      onDelete={handleDelete}
                    />
                  ))}
                </>
              )}

              {recentGroups.length > 0 && (
                <>
                  <div className="sb-section-h">최근</div>
                  {recentGroups.map((group) => (
                    <SidebarGroup
                      key={group.ticker}
                      group={group}
                      price={prices[group.ticker]}
                      pricesLoaded={pricesLoaded}
                      currentJobId={currentJobId}
                      favorited={false}
                      expanded={expandedTickers.has(group.ticker)}
                      onToggleExpand={() => toggleExpand(group.ticker)}
                      menuOpenId={menuOpenId}
                      onMenuToggle={setMenuOpenId}
                      onFavToggle={() => toggleFav(group.latest.jobId)}
                      onDelete={handleDelete}
                    />
                  ))}
                </>
              )}

              {isLoggedIn && query && groups.length === 0 && (
                <div className="sb-empty" style={{ marginTop: 8 }}>
                  <p>"{query}" 결과 없음</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Bottom: credits + profile + theme toggle */}
        <div className="sb-bottom">
          {isLoggedIn && <CreditsBadge collapsed={collapsed} />}
          {/* 사용자 결정 #17: 다크/라이트 토글 */}
          <ThemeToggleRow collapsed={collapsed} />
          {isLoggedIn ? (
            <Link href="/mypage" prefetch={false} className="sb-profile" title={!collapsed ? undefined : "내 정보"}>
              <div className="sb-avatar">
                {"picture" in auth && auth.picture ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={auth.picture} alt="" />
                ) : (
                  <span>{("email" in auth ? auth.email : "?").slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              {!collapsed && (
                <div className="sb-profile-info">
                  <span className="sb-profile-name">{"name" in auth && auth.name ? auth.name : (auth as { email?: string }).email}</span>
                  <span className={`bs-tag bs-tag--xs bs-tag--${auth.tier || "free"}${auth.tier === "premium" ? " bs-tag--icon" : ""}`}>
                    {auth.tier === "premium" && <Sparkles className="w-3 h-3" />}
                    {auth.tier === "premium" ? "PREMIUM"
                      : auth.tier === "pro" ? "PRO"
                      : auth.tier === "basic" ? "BASIC"
                      : auth.tier === "beta" ? "BETA"
                      : auth.tier === "admin" ? "ADMIN"
                      : "FREE"}
                  </span>
                </div>
              )}
              {!collapsed && <Settings className="w-3.5 h-3.5 sb-profile-gear" />}
            </Link>
          ) : (
            !authLoading && (
              <Link href="/auth/google" className="sb-profile sb-profile-cta">
                <LogIn className="w-4 h-4" />
                {!collapsed && <span>로그인</span>}
              </Link>
            )
          )}
        </div>
      </aside>

      <style jsx global>{styleBlock}</style>
    </>
  );
}

// ── 다크/라이트 토글 (사용자 결정 #17) ──
function ThemeToggleRow({ collapsed }: { collapsed: boolean }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      className="sb-theme-toggle"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      aria-label={isDark ? "라이트 모드로 전환" : "다크 모드로 전환"}
      title={isDark ? "라이트 모드로 전환" : "다크 모드로 전환"}
    >
      {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      {!collapsed && <span>{isDark ? "라이트 모드" : "다크 모드"}</span>}
    </button>
  );
}

// ── 크레딧 아이콘 (Sparkles · AI 도파민 감성) ──
// 일반: 슬레이트 단색 sparkle
// 프리미엄: 골드 sparkle + 글로우 + 회전 펄스 (애니)
function CreditCoin({ size = 14, premium = false }: { size?: number; premium?: boolean }) {
  if (premium) {
    return (
      <span
        className="bs-credit-coin bs-credit-coin--premium"
        style={{ width: size, height: size, display: "inline-block", position: "relative" }}
        aria-hidden="true"
      >
        <Sparkles
          size={size}
          strokeWidth={2}
          style={{ color: "#f0b800", display: "block", filter: "drop-shadow(0 0 4px rgba(240, 184, 0, 0.5))" }}
        />
      </span>
    );
  }
  return (
    <Sparkles
      size={size}
      strokeWidth={2}
      style={{ color: "var(--bs-gray-500, #64748b)", display: "block" }}
      aria-hidden="true"
    />
  );
}

interface CreditsData {
  credits: number;
  premiumCredits: number;
  tier: string;
}

// ── 사이드바 하단 크레딧 잔액 표시 ──
function CreditsBadge({ collapsed }: { collapsed: boolean }) {
  const [data, setData] = useState<CreditsData | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    let active = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/credits/balance", { credentials: "include" });
        if (!res.ok || !active || !mountedRef.current) return;
        const d = (await res.json()) as CreditsData;
        if (active && mountedRef.current) setData(d);
      } catch { /* silent */ }
    }
    load();
    // 10초 폴링 + 'credits-updated' 이벤트로 즉시 갱신
    const t = setInterval(load, 10_000);
    const onUpdate = (e: Event) => {
      // 1) 낙관적 업데이트 — detail.delta 로 즉시 화면 반영
      const detail = (e as CustomEvent<{ delta?: { credits?: number; premiumCredits?: number } }>).detail;
      if (detail?.delta) {
        setData((prev) => prev ? {
          ...prev,
          credits: Math.max(0, (prev.credits ?? 0) + (detail.delta?.credits ?? 0)),
          premiumCredits: Math.max(0, (prev.premiumCredits ?? 0) + (detail.delta?.premiumCredits ?? 0)),
        } : prev);
      }
      // 2) 그 후 서버 값으로 sync (delay 짧게 — 서버 write 완료 대기)
      setTimeout(load, 500);
    };
    window.addEventListener("credits-updated", onUpdate);
    return () => {
      active = false;
      mountedRef.current = false;
      clearInterval(t);
      window.removeEventListener("credits-updated", onUpdate);
    };
  }, []);
  if (!data) return null;
  const fmt = (n: number) => n >= 1000 ? `${Math.floor(n / 1000)}K` : String(n);
  // 프리미엄 크레딧 0 일 때도 항상 표시 — 클릭하면 /pricing 으로 업그레이드 유도
  const hasPremium = data.premiumCredits > 0;
  const premiumPill = (
    <div
      className={`sb-credit-pill sb-credit-pill--premium ${hasPremium ? "" : "is-zero"}`}
      title={hasPremium
        ? `프리미엄 크레딧 ${data.premiumCredits}개 — 차트 분석 1회 = 10개`
        : "Premium 가입 시 매월 50개 충전 — AI 차트 분석 사용"}
    >
      <CreditCoin size={collapsed ? 12 : 14} premium />
      <span className="sb-credit-num">{collapsed ? fmt(data.premiumCredits) : data.premiumCredits.toLocaleString("ko-KR")}</span>
    </div>
  );
  if (collapsed) {
    return (
      <div className="sb-credits sb-credits--collapsed" title={`크레딧 ${data.credits} · 프리미엄 ${data.premiumCredits}`}>
        <div className="sb-credit-pill" title={`크레딧 ${data.credits}개 — 보고서 1편 = 30개`}>
          <CreditCoin size={12} />
          <span className="sb-credit-num">{fmt(data.credits)}</span>
        </div>
        {hasPremium ? premiumPill : (
          <Link href="/pricing" className="sb-credit-link">{premiumPill}</Link>
        )}
      </div>
    );
  }
  return (
    <div className="sb-credits">
      <Link href="/pricing" className="sb-credit-pill" title={`크레딧 ${data.credits}개 — 클릭하면 구독 페이지로`}>
        <CreditCoin size={14} />
        <span className="sb-credit-num">{data.credits.toLocaleString("ko-KR")}</span>
      </Link>
      <Link href="/pricing" className="sb-credit-link">
        {premiumPill}
      </Link>
    </div>
  );
}

// ── 등락률 표시 (로딩 skeleton / null 숨김 / 값) ──
function PctBadge({ price, loading }: { price?: PriceInfo; loading: boolean }) {
  if (loading && price === undefined) {
    return <span className="bs-skel" style={{ marginLeft: "auto", width: 44, height: 10, borderRadius: 3 }} aria-label="로딩 중" />;
  }
  const pct = price?.changePercent;
  if (pct == null) return null; // 데이터 없으면 빈 공간 — 대시 안 표시
  const color = pct > 0 ? "var(--bs-up)" : pct < 0 ? "var(--bs-down)" : "var(--bs-gray-500)";
  const label = `${pct > 0 ? "+" : ""}${(pct * 100).toFixed(2)}%`;
  return <span className="sb-row-pct" style={{ color }}>{label}</span>;
}

// ── Group Row (같은 ticker의 분석 버전들을 묶음) ──
function SidebarGroup({
  group, price, pricesLoaded, currentJobId, favorited, expanded,
  onToggleExpand, menuOpenId, onMenuToggle, onFavToggle, onDelete,
}: {
  group: TickerGroup;
  price?: PriceInfo;
  pricesLoaded: boolean;
  currentJobId: string;
  favorited: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  menuOpenId: string | null;
  onMenuToggle: (id: string | null) => void;
  onFavToggle: () => void;
  onDelete: (jobId: string) => void;
}) {
  const hasVersions = group.versions.length > 1;
  const activeVersionIdx = group.versions.findIndex((v) => v.jobId === currentJobId);
  const headerActive = activeVersionIdx === 0 || (!expanded && activeVersionIdx >= 0);
  const menuOpen = menuOpenId === group.latest.jobId;

  return (
    <>
      <div className={`sb-row ${headerActive ? "on" : ""}`}>
        <Link
          href={`/analyze/${encodeURIComponent(group.ticker)}`}
          className="sb-row-link"
        >
          <div className="sb-row-top">
            <span className="sb-row-tkr">{group.ticker}</span>
            {hasVersions && (
              <span
                className={`sb-row-ver ${expanded ? "open" : ""}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleExpand(); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault(); e.stopPropagation(); onToggleExpand();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-label={expanded ? "버전 접기" : `버전 ${group.versions.length}개 펼치기`}
                title={`분석 ${group.versions.length}개`}
              >
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                <span className="sb-row-ver-num">v{group.versions.length}</span>
              </span>
            )}
            <PctBadge price={price} loading={!pricesLoaded} />
          </div>
          <div className="sb-row-bot">
            <span className="sb-row-nm">{group.companyName || "—"}</span>
            <span className="sb-row-sep">·</span>
            <TimeAgoSpan iso={group.latest.createdAt} className="sb-row-time" />
          </div>
        </Link>
        <button
          type="button"
          className="sb-row-menu-btn"
          onClick={(e) => {
            e.preventDefault();
            onMenuToggle(menuOpen ? null : group.latest.jobId);
          }}
          aria-label="Options"
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
        {menuOpen && (
          <div className="sb-row-menu" onMouseLeave={() => onMenuToggle(null)}>
            <button onClick={() => { onFavToggle(); onMenuToggle(null); }}>
              <Star className="w-3.5 h-3.5" fill={favorited ? "currentColor" : "none"} />
              {favorited ? "즐겨찾기 해제" : "즐겨찾기"}
            </button>
            <button onClick={() => onDelete(group.latest.jobId)} className="danger">
              <Trash2 className="w-3.5 h-3.5" />
              {hasVersions ? "최신 버전 제거" : "제거"}
            </button>
          </div>
        )}
      </div>

      {hasVersions && expanded && (
        <div className="sb-versions">
          {group.versions.map((v, idx) => {
            const verNum = group.versions.length - idx; // 가장 최근이 가장 큰 번호
            const isActive = currentJobId === v.jobId;
            const verMenuOpen = menuOpenId === v.jobId;
            return (
              <div key={v.jobId} className={`sb-version ${isActive ? "on" : ""}`}>
                <Link
                  href={`/bite/result?id=${encodeURIComponent(v.jobId)}`}
                  className="sb-ver-link"
                >
                  <span className="sb-ver-num">v{verNum}</span>
                  <TimeAgoSpan iso={v.createdAt} className="sb-ver-time" />
                  {idx === 0 && <span className="sb-ver-latest">최신</span>}
                </Link>
                <button
                  type="button"
                  className="sb-ver-menu-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    onMenuToggle(verMenuOpen ? null : v.jobId);
                  }}
                  aria-label="Options"
                >
                  <MoreHorizontal className="w-3 h-3" />
                </button>
                {verMenuOpen && (
                  <div className="sb-row-menu" onMouseLeave={() => onMenuToggle(null)}>
                    <button onClick={() => onDelete(v.jobId)} className="danger">
                      <Trash2 className="w-3.5 h-3.5" />
                      이 버전 제거
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

const styleBlock = `
  .app-sb {
    /* 사용자 결정 2026-05-09 — 화면 좌측 flush (좌측만 8px margin 비대칭 결함 수정).
       사이드바 = full-height bar, 카드 형태 폐기.
       position fixed — body flex-col 안에서 normal flow 자리 차지하면 main 이 아래로
       밀려나는 결함 수정. fixed 로 main 위에 띄움. */
    width: 260px;
    height: 100vh;
    position: fixed; top: 0; left: 0;
    z-index: 40;
    margin: 0;
    border-radius: 0;
    border: 0;
    box-shadow: none;
    /* 배경: shell-ws[data-bg] 기준 반대 톤 — gray main → white sidebar / white main → gray sidebar
       (사용자 결정: 자동 감지로 부드럽게 적응) */
    background: var(--bs-paper);
    display: flex; flex-direction: column;
    flex-shrink: 0;
    /* 부드러운 expand/collapse — Apple-style cubic-bezier (사용자 결정 #4) */
    transition:
      width 280ms cubic-bezier(0.32, 0.72, 0.32, 1),
      background 240ms cubic-bezier(0.32, 0.72, 0.32, 1);
    overflow: hidden;
  }
  /* 디폴트 sidebar = 흰색 (gray-main 페이지 가정 — 홈/대시보드/차트). */
  /* 메인이 흰색인 페이지에서는 sidebar 를 회색으로 (대비). 자동 감지 — pathname 하드코딩 X.
     자식 페이지의 wrapper class 가 gray-bg 가 아니면 (= white-main) sidebar=gray.
     역명제: shell-ws 가 .home-onepage / .cl-wrap / [data-page-bg=gray] 를 갖지 않을 때.
     CSS :not(:has(...)) 로 표현. */
  .shell-ws:not(:has(.home-onepage)):not(:has(.cl-wrap)):not(:has([data-page-bg="gray"])) .app-sb {
    background: var(--bs-gray-50);
  }

  /* 사용자 결정 2026-05-09 — 라벨 (작은 글자) 들어갈 폭 확보 */
  .app-sb.collapsed { width: 76px; }
  /* ── palette 모드 (대시보드 편집) — 사이드바를 위젯 갤러리 전용 폭으로 확장 ── */
  .app-sb.palette { width: 340px; }
  .sb-palette-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 14px 10px;
    border-bottom: 1px solid var(--bs-gray-200);
  }
  .sb-palette-title {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--bs-ink);
  }
  .sb-palette-title :global(svg) { color: var(--bs-gray-600); }
  .sb-palette-done {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 4px 10px; border: 0;
    background: var(--bs-ink); color: var(--bs-paper);
    border-radius: 999px;
    font-size: 11px; font-weight: 700;
    cursor: pointer; font-family: inherit;
    transition: background 140ms;
  }
  .sb-palette-done:hover { background: var(--bs-gray-700); }
  .sb-palette-body {
    flex: 1; overflow-y: auto;
    padding: 4px 2px 14px;
    scrollbar-width: thin;
  }
  /* palette 안에 있는 WidgetPalette 의 기본 dash-palette-inline 스타일 미세 조정 */
  .app-sb.palette .dash-palette-inline { padding: 0 12px; }
  .app-sb.palette .dash-palette-head { padding: 12px 2px 8px; }
  .app-sb.palette .dash-palette-head h2 { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--bs-gray-500); margin: 0 0 2px; }
  .app-sb.palette .dash-palette-hint { font-size: 10px; color: var(--bs-gray-500); margin: 0; }
  .app-sb.palette .dash-palette-grid { grid-template-columns: 1fr 1fr; gap: 6px; }
  .app-sb.palette .dash-palette-item { padding: 8px 9px; min-height: 68px; }
  .app-sb.palette .dash-palette-icon { font-size: 16px; }
  .app-sb.palette .dash-palette-name { font-size: 12px; }
  .app-sb.palette .dash-palette-desc { font-size: 10px; line-height: 1.35; }

  /* inner 블록들은 expanded 기준(240px 내외)으로 항상 고정 폭 — wrap 방지 */
  .app-sb .sb-top,
  .app-sb .sb-new,
  .app-sb .sb-search,
  .app-sb .sb-list,
  .app-sb .sb-bottom { min-width: 240px; }
  /* 접힌 상태에서만 sb-top/sb-new/sb-bottom 을 좁은 폭 허용 (아이콘만) */
  .app-sb.collapsed .sb-top,
  .app-sb.collapsed .sb-new,
  .app-sb.collapsed .sb-bottom { min-width: 0; }

  .app-sb-mobile-toggle {
    display: none;
    /* 원형 · 축소 (기존 38×38 사각 → 32×32 원). 컨텐츠를 크게 밀지 않음. */
    position: fixed; top: 10px; left: 10px; z-index: 60;
    width: 32px; height: 32px; border-radius: 50%;
    border: 1px solid var(--bs-gray-200);
    background: var(--bs-paper); color: var(--bs-ink);
    align-items: center; justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(17, 17, 17, 0.08);
  }
  /* 사이드바가 열리면 토글 버튼은 숨김 (JS state 로 .is-hidden 부여) */
  .app-sb-mobile-toggle.is-hidden { display: none !important; }
  .app-sb-backdrop {
    display: none;
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.3);
    z-index: 35;
  }

  /* Top — 로고 (좌) · 토글 버튼 (우 상시 노출, 껍질 없음) */
  .sb-top {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 14px 8px;
    min-height: 40px;
  }
  /* Header .brand 와 동일 스타일 (font-size 11 · uppercase · letter-spacing 0.2em) */
  .sb-logo {
    display: inline-flex; align-items: center; gap: 8px;
    text-decoration: none; color: var(--bs-ink);
    font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.2em;
  }
  .sb-logo-mark {
    width: 20px; height: 20px;
    object-fit: contain;
    display: inline-block;
    flex-shrink: 0;
  }
  .sb-logo-text {
    font-size: 11px; font-weight: 600;
    letter-spacing: 0.2em; text-transform: uppercase;
  }
  /* 토글 버튼 — 껍질 없는 아이콘만 · 우측 상단 상시 노출 */
  .sb-collapse {
    border: 0; cursor: pointer;
    width: 24px; height: 24px;
    background: transparent; color: var(--bs-gray-500);
    display: inline-flex; align-items: center; justify-content: center;
    transition: color 140ms ease;
    padding: 0;
  }
  .sb-collapse:hover { color: var(--bs-ink); }

  /* 접힌 상태: 로고 숨기고 토글 버튼만 가운데 */
  .app-sb.collapsed .sb-top { justify-content: center; padding: 14px 0 8px; }
  .app-sb.collapsed .sb-logo { display: none; }

  /* New analysis — 검색 아래에 배치 · 껍질(배경/border) 없는 가벼운 row */
  .sb-new {
    margin: 4px 10px 4px;
    display: flex; align-items: center; gap: 10px;
    padding: 8px 12px; border-radius: 8px;
    background: transparent;
    color: var(--bs-ink);
    border: 0;
    font-size: 13px; font-weight: 600;
    text-decoration: none;
    transition: background 120ms;
  }
  .sb-new:hover {
    background: var(--bs-gray-100);
  }
  .sb-new .sb-kbd {
    margin-left: auto;
    padding: 2px 6px; font-size: 10px;
    background: var(--bs-gray-100); color: var(--bs-gray-600);
    border-radius: 4px; font-family: ui-monospace, monospace;
  }

  /* ── Main Nav (메인 메뉴 5개) ── */
  /* divider 폐기 (사용자 결정) — spacing 만으로 다음 섹션과 분리. */
  .sb-nav {
    display: flex; flex-direction: column;
    gap: 1px;
    margin: 10px 10px 16px;
  }
  .app-sb.collapsed .sb-nav {
    margin: 10px 6px 16px;
  }
  .sb-nav-item {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 12px;
    border: 0; background: transparent;
    border-radius: 8px;
    color: var(--bs-gray-700);
    font-size: 13px; font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    transition: background 120ms, color 120ms;
    width: 100%;
  }
  .sb-nav-item:hover {
    background: var(--bs-gray-100);
    color: var(--bs-ink);
  }
  /* 활성 탭 — 기업 row selected (.sb-row.on) 과 동일 톤 (gray-200 · 얕은 회색) */
  .sb-nav-item.is-active {
    background: var(--bs-gray-200);
    color: var(--bs-ink);
    font-weight: 600;
  }
  .sb-nav-item.is-active:hover {
    background: var(--bs-gray-200);
    color: var(--bs-ink);
  }
  .sb-nav-item :global(svg) {
    flex-shrink: 0;
  }
  .sb-nav-label {
    flex: 1;
    letter-spacing: -0.005em;
  }
  .sb-nav-badge {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--bs-gray-500);
    background: var(--bs-gray-100);
    padding: 2px 5px;
    border-radius: 3px;
  }
  .sb-nav-item-coming {
    color: var(--bs-gray-400);
    opacity: 0.55;
  }
  .sb-nav-item-coming:hover {
    background: var(--bs-gray-50);
    color: var(--bs-gray-500);
    opacity: 0.7;
  }
  .sb-nav-item-coming :global(svg) { opacity: 0.7; }
  .sb-nav-item-coming .sb-nav-badge {
    background: var(--bs-gray-100);
    color: var(--bs-gray-400);
    border: 1px solid var(--bs-gray-200);
  }

  /* Premium tone — BITE / ENTRY (사용자 결정 — 그라데이션 폐기, 아이콘 컬러만으로 강조).
     배경/라벨 weight 는 일반 nav-item 과 동일. 강조는 브랜드 컬러 아이콘만 (Linear/Vercel 패턴).
     첫 번째 child = Icon 컴포넌트. svg/span 모두 currentColor 사용하므로 부모 color 만 지정. */
  .sb-nav-item-premium-red > :first-child {
    color: #FF1F2D;   /* BITE red */
  }
  .sb-nav-item-premium-blue > :first-child {
    color: #0B2447;   /* ENTRY navy */
  }

  /* collapsed 상태 — 다른 nav 와 동일 padding (override X) */
  /* 사용자 결정 2026-05-09 — collapsed 시 sb-nav-item: 아이콘 + 라벨 stack (세로) */
  .app-sb.collapsed .sb-nav-item {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding: 8px 4px;
  }
  .app-sb.collapsed .sb-nav-label {
    font-size: 10px;
    line-height: 1.1;
    font-weight: 600;
    text-align: center;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    max-width: 100%;
  }

  /* Search — pill 형태 (사용자 결정 #7): border-radius 999px, 테두리 0 */
  .sb-search {
    margin: 0 10px 10px;
    display: flex; align-items: center; gap: 6px;
    padding: 0 14px; height: 38px;
    border-radius: 999px;
    background: var(--bs-gray-100);
    border: 0;
    transition: background 160ms cubic-bezier(0.32, 0.72, 0.32, 1);
  }
  .sb-search:focus-within {
    background: var(--bs-gray-200);
  }
  /* sidebar 가 회색일 때 (white-main 페이지) — 검색창 배경 흰색으로 대비 */
  .shell-ws:not(:has(.home-onepage)):not(:has(.cl-wrap)):not(:has([data-page-bg="gray"])) .app-sb .sb-search { background: var(--bs-paper); }
  .shell-ws:not(:has(.home-onepage)):not(:has(.cl-wrap)):not(:has([data-page-bg="gray"])) .app-sb .sb-search:focus-within { background: var(--bs-paper); }
  .sb-search > :global(svg) { color: var(--bs-gray-500); flex-shrink: 0; }
  .sb-search input {
    flex: 1; border: 0; outline: 0; background: transparent;
    font-size: 12px; color: var(--bs-ink);
    font-family: inherit;
    min-width: 0;
  }
  .sb-search input::placeholder { color: var(--bs-gray-500); }
  .sb-search button {
    border: 0; background: transparent; cursor: pointer;
    color: var(--bs-gray-500); padding: 2px;
  }
  .sb-kbd {
    padding: 2px 6px; font-size: 10px;
    background: var(--bs-gray-100); color: var(--bs-gray-600);
    border-radius: 4px; font-family: ui-monospace, monospace;
  }

  /* List — 스크롤 가능하지만 드래그바는 숨김 (휠/트랙패드로만 스크롤) */
  .sb-list {
    flex: 1; overflow-y: auto;
    padding: 0 6px 10px;
    scrollbar-width: none; /* Firefox */
  }
  .sb-list::-webkit-scrollbar { display: none; /* Chrome/Safari */ }

  .sb-section-h {
    display: flex; align-items: center; gap: 6px;
    padding: 12px 10px 6px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--bs-gray-500);
  }
  .sb-section-h :global(svg) { color: var(--bs-gray-500); }

  /* Row */
  .sb-row {
    position: relative;
    border-radius: 8px;
    transition: background 120ms;
  }
  .sb-row:hover { background: var(--bs-gray-100); }
  .sb-row.on { background: var(--bs-gray-200); }
  .sb-row-link {
    display: block; padding: 8px 12px;
    text-decoration: none; color: var(--bs-ink);
  }
  .sb-row-top {
    display: flex; align-items: baseline; gap: 8px;
    margin-bottom: 2px;
  }
  .sb-row-tkr {
    font-size: 13px; font-weight: 700; letter-spacing: -0.005em;
    color: var(--bs-ink);
    font-variant-numeric: tabular-nums;
  }
  .sb-row-pct {
    margin-left: auto;
    font-size: 11px; font-weight: 600;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }
  /* .sb-row-pct-skel · @keyframes sbSkelPulse → .bs-skel (bullstory-tokens.css) */

  /* 버전 카운터 (ticker 옆) */
  .sb-row-ver {
    display: inline-flex; align-items: center; gap: 2px;
    padding: 2px 5px 2px 3px;
    font-size: 10px; font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--bs-gray-600);
    background: var(--bs-gray-100);
    border: 1px solid var(--bs-gray-200);
    border-radius: 4px;
    cursor: pointer;
    transition: background 120ms, color 120ms;
  }
  .sb-row-ver:hover { background: var(--bs-gray-200); color: var(--bs-ink); }
  .sb-row-ver.open { background: var(--bs-ink); color: #fff; border-color: var(--bs-ink); }
  .sb-row-ver.open :global(svg), .sb-row-ver.open .sb-row-ver-num { color: #fff; }
  .sb-row-ver :global(svg) { flex-shrink: 0; }
  .sb-row-ver-num { letter-spacing: -0.01em; }

  /* 버전 목록 */
  .sb-versions {
    margin: 2px 6px 4px 18px;
    padding-left: 10px;
    border-left: 1px solid var(--bs-gray-200);
    display: flex; flex-direction: column; gap: 1px;
  }
  .sb-version {
    position: relative;
    display: flex; align-items: center;
    border-radius: 6px;
    transition: background 120ms;
  }
  .sb-version:hover { background: var(--bs-gray-100); }
  .sb-version.on { background: var(--bs-gray-200); }
  .sb-ver-link {
    flex: 1; min-width: 0;
    display: flex; align-items: center; gap: 8px;
    padding: 6px 28px 6px 10px;
    text-decoration: none; color: var(--bs-gray-700);
    font-size: 11px;
  }
  .sb-version.on .sb-ver-link { color: var(--bs-ink); font-weight: 600; }
  .sb-ver-num {
    font-variant-numeric: tabular-nums;
    color: var(--bs-gray-500);
    font-weight: 600;
    flex-shrink: 0;
    min-width: 22px;
  }
  .sb-version.on .sb-ver-num { color: var(--bs-ink); }
  .sb-ver-time {
    flex: 1; min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .sb-ver-latest {
    font-size: 9px; font-weight: 700;
    letter-spacing: 0.06em;
    color: var(--bs-up);
    padding: 1px 5px;
    background: color-mix(in srgb, var(--bs-up) 14%, transparent);
    border-radius: 3px;
    flex-shrink: 0;
  }
  .sb-ver-menu-btn {
    position: absolute; right: 4px; top: 50%;
    transform: translateY(-50%);
    width: 20px; height: 20px; border-radius: 4px;
    border: 0; background: transparent;
    color: var(--bs-gray-500); cursor: pointer;
    opacity: 0; transition: opacity 120ms;
    display: flex; align-items: center; justify-content: center;
  }
  .sb-version:hover .sb-ver-menu-btn, .sb-ver-menu-btn:focus { opacity: 1; }
  .sb-ver-menu-btn:hover { background: var(--bs-gray-200); color: var(--bs-ink); }
  .sb-row-bot {
    display: flex; align-items: center; gap: 5px;
    font-size: 11px; color: var(--bs-gray-500);
    min-width: 0;
  }
  .sb-row-nm {
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    flex: 1; min-width: 0;
  }
  .sb-row-sep { color: var(--bs-gray-400); }
  .sb-row-time { flex-shrink: 0; }

  .sb-row-menu-btn {
    position: absolute; right: 4px; top: 4px;
    width: 20px; height: 20px; border-radius: 5px;
    border: 0; background: transparent;
    color: var(--bs-gray-500); cursor: pointer;
    opacity: 0; transition: opacity 120ms, background 120ms;
    display: flex; align-items: center; justify-content: center;
    z-index: 1;
  }
  .sb-row:hover .sb-row-menu-btn, .sb-row-menu-btn:focus { opacity: 1; }
  .sb-row-menu-btn:hover { background: var(--bs-gray-200); color: var(--bs-ink); }

  .sb-row-menu {
    position: absolute; right: 8px; top: 100%;
    margin-top: 2px;
    background: var(--bs-paper);
    border: 1px solid var(--bs-gray-200);
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
    padding: 4px; min-width: 140px; z-index: 10;
  }
  .sb-row-menu button {
    display: flex; align-items: center; gap: 8px;
    width: 100%; padding: 7px 10px; border: 0; border-radius: 6px;
    background: transparent; color: var(--bs-ink);
    font-size: 12px; font-family: inherit; text-align: left;
    cursor: pointer;
  }
  .sb-row-menu button:hover { background: var(--bs-gray-100); }
  .sb-row-menu button.danger { color: var(--bs-down); }
  .sb-row-menu button.danger:hover { background: color-mix(in srgb, var(--bs-down) 10%, transparent); }

  /* Empty */
  .sb-empty {
    padding: 24px 18px; text-align: center;
    color: var(--bs-gray-600);
  }
  .sb-empty-icon { display: inline-block; color: var(--bs-gray-400); margin-bottom: 8px; }
  .sb-empty p { margin: 0 0 10px; font-size: 12px; line-height: 1.5; }
  .sb-empty-cta {
    display: inline-block; padding: 6px 12px; border-radius: 999px;
    background: var(--bs-ink); color: #fff;
    font-size: 11px; font-weight: 600; text-decoration: none;
  }
  .sb-sample { display: flex; gap: 6px; justify-content: center; flex-wrap: wrap; }
  .sb-sample-chip {
    padding: 4px 10px; border-radius: 999px;
    background: var(--bs-paper); border: 1px solid var(--bs-gray-200);
    color: var(--bs-ink);
    font-size: 11px; font-weight: 600;
    text-decoration: none;
    font-variant-numeric: tabular-nums;
  }
  .sb-sample-chip:hover { background: var(--bs-gray-100); }

  /* Bottom */
  .sb-bottom {
    padding: 10px;
    border-top: 1px solid var(--bs-gray-200);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  /* Credits — 캡슐/테두리/배경 모두 제거 · 코인 + 숫자만 (미니멀) */
  .sb-credits {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 6px 10px;
    text-decoration: none;
  }
  .sb-credits--collapsed {
    flex-direction: column;
    gap: 8px;
    padding: 4px;
    align-items: center;
  }
  .sb-credit-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 0;
    background: transparent;
    border: 0;
    text-decoration: none;
    font-size: 12px;
    font-weight: 700;
    color: var(--bs-ink);
    font-variant-numeric: tabular-nums;
    transition: opacity 0.15s ease;
  }
  .sb-credit-pill:hover { opacity: 0.7; }
  .sb-credit-pill--premium { color: #b48518; }
  .sb-credit-pill--premium.is-zero {
    color: var(--bs-gray-500);
    opacity: 0.5;
  }
  .sb-credit-pill--premium.is-zero :global(svg) {
    filter: grayscale(0.85);
  }
  .sb-credit-link { display: inline-flex; text-decoration: none; }
  .sb-credit-link:hover .sb-credit-pill--premium.is-zero {
    opacity: 1;
    color: #b48518;
  }
  .sb-credit-link:hover .sb-credit-pill--premium.is-zero :global(svg) {
    filter: none;
  }
  .sb-credit-num { white-space: nowrap; }
  /* 프리미엄 코인 sparkle 애니메이션 — 4-point star 가 페이드 in/out */
  .bs-credit-coin--premium { position: relative; display: inline-block; }
  .bs-credit-coin--premium :global(.bs-coin-spark) {
    transform-origin: center;
    animation: bs-coin-sparkle 2.4s ease-in-out infinite;
  }
  .bs-credit-coin--premium :global(.bs-coin-spark--2) {
    animation-delay: 0.8s;
  }
  .bs-credit-coin--premium :global(.bs-coin-spark--3) {
    animation-delay: 1.6s;
  }
  @keyframes bs-coin-sparkle {
    0%, 100% { opacity: 0; transform: scale(0.6); }
    50%      { opacity: 1; transform: scale(1.3); }
  }
  /* 프리미엄 코인 자체에 펄스 글로우 */
  .bs-credit-coin--premium::before {
    content: "";
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 217, 106, 0.35) 0%, transparent 70%);
    animation: bs-coin-glow 2.4s ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes bs-coin-glow {
    0%, 100% { opacity: 0.5; transform: scale(0.85); }
    50%      { opacity: 1;   transform: scale(1.15); }
  }
  /* is-zero 일 땐 sparkle / glow 숨김 */
  .sb-credit-pill--premium.is-zero .bs-credit-coin--premium :global(.bs-coin-spark) { animation: none; opacity: 0; }
  .sb-credit-pill--premium.is-zero .bs-credit-coin--premium::before { animation: none; opacity: 0; }
  .sb-credit-link:hover .sb-credit-pill--premium.is-zero .bs-credit-coin--premium :global(.bs-coin-spark) { animation: bs-coin-sparkle 2.4s ease-in-out infinite; opacity: 1; }
  .sb-credit-link:hover .sb-credit-pill--premium.is-zero .bs-credit-coin--premium::before { animation: bs-coin-glow 2.4s ease-in-out infinite; opacity: 1; }
  .sb-profile {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border-radius: 10px;
    text-decoration: none; color: var(--bs-ink);
  }
  .sb-profile:hover { background: var(--bs-gray-100); }
  /* 사용자 결정 #17: 사이드바 다크/라이트 토글 */
  .sb-theme-toggle {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px;
    background: transparent;
    border: 0;
    border-radius: 10px;
    color: var(--bs-gray-600);
    cursor: pointer;
    font-size: 13px;
    width: 100%;
    text-align: left;
    transition: background 120ms;
  }
  .sb-theme-toggle:hover { background: var(--bs-gray-100); color: var(--bs-ink); }
  .app-sb.collapsed .sb-theme-toggle { justify-content: center; padding: 8px; }
  .sb-avatar {
    width: 28px; height: 28px; border-radius: 50%;
    background: var(--bs-gray-200); color: var(--bs-gray-700);
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; flex-shrink: 0; overflow: hidden;
  }
  .sb-avatar :global(img) { width: 100%; height: 100%; object-fit: cover; }
  .sb-profile-info {
    min-width: 0; flex: 1;
    display: flex; flex-direction: column;
    align-items: flex-start; /* tag 가 container 폭만큼 stretch 되는 버그 방지 */
    gap: 4px;
  }
  .sb-profile-name {
    max-width: 100%;
    font-size: 12px; font-weight: 600;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  /* .sb-tier · .sb-tier-* → .bs-tag.bs-tag--xs.bs-tag--{free|pro|premium|admin}
     (bullstory-tokens.css) */
  .sb-profile-gear {
    color: var(--bs-gray-500);
    flex-shrink: 0;
  }
  .sb-profile-cta { background: var(--bs-ink); color: #fff; font-size: 12px; font-weight: 600; }
  .sb-profile-cta:hover { background: var(--bs-gray-700); }

  @media (max-width: 900px) {
    .app-sb {
      position: fixed; left: 0; top: 0; bottom: 0; height: 100vh;
      z-index: 50;
      transform: translateX(-100%);
    }
    .app-sb.mobile-open { transform: translateX(0); }
    .app-sb.collapsed { width: 260px; }
    .app-sb-mobile-toggle { display: inline-flex; }
    .app-sb.mobile-open ~ .app-sb-backdrop,
    .app-sb-backdrop { display: block; }
  }
  @media (min-width: 901px) {
    .app-sb-mobile-toggle, .app-sb-backdrop { display: none !important; }
  }
`;
